import { memo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import type { Rating } from '@/api/types/calibrationMatrix.types';
import {
  getRatingBadgeStyle,
  getWindowTierLabel,
  type WindowTier,
} from './helpers';

export interface CalibrationMatrixCellConsultant {
  appraisalId: number;
  firstName: string;
  lastName: string;
  geo: string;
}

interface CalibrationMatrixCellProps {
  column: string;
  rating: Rating;
  billingGrade: string;
  consultants: CalibrationMatrixCellConsultant[];
  windowTier: WindowTier | null;
  outOfRegion?: boolean;
}

function getCellBackground(
  theme: Theme,
  windowTier: WindowTier | null,
  hasConsultants: boolean,
  outOfRegion: boolean
): string {
  if (windowTier === 'formalElectcomm') return theme.palette.custom.gold;
  if (windowTier === 'electcomm') return theme.palette.custom.cappuccino;
  if (windowTier === 'promotion') return theme.palette.custom.lightPeach;
  if (outOfRegion) return theme.palette.grey[300];
  if (hasConsultants) return theme.palette.grey[100];
  return theme.palette.background.paper;
}

function CalibrationMatrixCellInner({
  column,
  rating,
  billingGrade,
  consultants,
  windowTier,
  outOfRegion = false,
}: CalibrationMatrixCellProps) {
  const theme = useTheme();
  const bgcolor = getCellBackground(theme, windowTier, consultants.length > 0, outOfRegion);
  const tierLabel = getWindowTierLabel(windowTier);
  const ratingBadge = getRatingBadgeStyle(rating);

  const namesText = consultants
    .map((c) => `${c.firstName} ${c.lastName}${c.geo ? ` (${c.geo})` : ''}`)
    .join(', ');
  const ariaParts: string[] = [
    `Tenure ${column}`,
    `Rating ${rating}`,
    `Billing Grade ${billingGrade}`,
  ];
  if (tierLabel) ariaParts.push(tierLabel);
  ariaParts.push(
    consultants.length === 0
      ? 'no consultants'
      : `${consultants.length} consultant${consultants.length === 1 ? '' : 's'}: ${namesText}`
  );

  return (
    <Box
      role="gridcell"
      aria-label={ariaParts.join(', ')}
      data-testid={`matrix-cell-${column}-${rating}-${billingGrade}`}
      data-testid-cell="matrix-cell"
      data-window-tier={windowTier ?? 'none'}
      sx={{
        position: 'relative',
        bgcolor,
        border: 1,
        borderColor: 'divider',
        p: 0.5,
        minHeight: 48,
        backgroundColor: bgcolor,
      }}
      style={{ backgroundColor: bgcolor }}
    >
      {!outOfRegion && (
        <Box
          data-testid={`rating-badge-${rating}`}
          aria-hidden
          sx={{
            position: 'absolute',
            top: 2,
            right: 4,
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: ratingBadge.bgcolor,
            color: ratingBadge.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.6875rem',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {rating}
        </Box>
      )}
      <Stack
        spacing={0}
        sx={{
          pt: outOfRegion ? 0 : 3,
        }}
      >
        {consultants.map((c) => (
          <Typography key={c.appraisalId} variant="body2" sx={{ lineHeight: 1.3 }}>
            {c.firstName} {c.lastName}
            {c.geo ? ` (${c.geo})` : ''}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export const CalibrationMatrixCell = memo(CalibrationMatrixCellInner);

export default CalibrationMatrixCell;
